import { useCallback, useEffect, useState } from 'react';
import { Button, Form, Input, Modal, Popconfirm, Space, Switch, Table, message } from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';

import {
  createCategory,
  deleteCategory,
  listCategoriesAdmin,
  reorderCategories,
  updateCategory,
  type CategoryAdmin,
  type CategoryWritePayload,
} from '../../services/admin';
import { apiErrorMessage } from './desafios';
import { moveItem } from './move-item';

interface CategoryFormValues {
  name: string;
  tagline?: string;
  active: boolean;
  showInOnboarding: boolean;
}

const isNameTaken = (err: unknown): boolean => {
  const response = (err as { response?: { status?: number; data?: { message?: unknown } } })
    ?.response;
  return response?.status === 409 && response.data?.message === 'category_name_taken';
};

interface CategoriasCatalogProps {
  /** Quando a aba fica ativa a lista é relida (contagens frescas). */
  active?: boolean;
}

export const CategoriasCatalog: React.FC<CategoriasCatalogProps> = ({ active = true }) => {
  const [categories, setCategories] = useState<CategoryAdmin[]>([]);
  const [loading, setLoading] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CategoryAdmin | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<CategoryFormValues>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setCategories(await listCategoriesAdmin());
    } catch {
      message.error('Falha ao carregar as categorias.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (active) load();
  }, [active, load]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ active: true, showInOnboarding: true });
    setModalOpen(true);
  };

  const openEdit = (c: CategoryAdmin) => {
    setEditing(c);
    form.setFieldsValue({
      name: c.name,
      tagline: c.tagline ?? '',
      active: c.active,
      showInOnboarding: c.showInOnboarding,
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    let values: CategoryFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const payload: CategoryWritePayload = {
      name: values.name.trim(),
      tagline: values.tagline?.trim() ? values.tagline.trim() : null,
      active: values.active,
      showInOnboarding: values.showInOnboarding,
    };
    setSaving(true);
    try {
      if (editing) {
        await updateCategory(editing.id, payload);
        message.success('Categoria atualizada.');
      } else {
        await createCategory(payload);
        message.success('Categoria criada.');
      }
      setModalOpen(false);
      await load();
    } catch (err: unknown) {
      message.error(
        isNameTaken(err)
          ? 'Já existe uma categoria com esse nome.'
          : apiErrorMessage(err) ?? 'Falha ao salvar a categoria.',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (c: CategoryAdmin) => {
    try {
      await deleteCategory(c.id);
      message.success('Categoria excluída.');
      await load();
    } catch (err: unknown) {
      message.error(apiErrorMessage(err) ?? 'Falha ao excluir a categoria.');
    }
  };

  const handleToggle = async (
    c: CategoryAdmin,
    patch: Pick<CategoryWritePayload, 'active' | 'showInOnboarding'>,
  ) => {
    setBusyId(c.id);
    try {
      await updateCategory(c.id, patch);
      await load();
    } catch (err: unknown) {
      message.error(apiErrorMessage(err) ?? 'Falha ao salvar a categoria.');
    } finally {
      setBusyId(null);
    }
  };

  const handleMove = async (index: number, delta: -1 | 1) => {
    const next = moveItem(categories, index, delta);
    if (next === categories) return;
    setCategories(next);
    setReordering(true);
    try {
      setCategories(await reorderCategories(next.map((c) => c.id)));
      message.success('Ordem salva.');
    } catch {
      message.error('Falha ao reordenar.');
      await load();
    } finally {
      setReordering(false);
    }
  };

  const columns = [
    {
      title: 'Ordem',
      key: 'ordem',
      width: 96,
      render: (_: unknown, c: CategoryAdmin, index: number) => (
        <Space size={4}>
          <Button
            size="small"
            aria-label={`Subir ${c.name}`}
            icon={<ArrowUpOutlined />}
            disabled={index === 0 || reordering}
            onClick={() => handleMove(index, -1)}
          />
          <Button
            size="small"
            aria-label={`Descer ${c.name}`}
            icon={<ArrowDownOutlined />}
            disabled={index === categories.length - 1 || reordering}
            onClick={() => handleMove(index, 1)}
          />
        </Space>
      ),
    },
    { title: 'Nome', dataIndex: 'name' },
    {
      title: 'Frase',
      dataIndex: 'tagline',
      render: (v: string | null) => v ?? '—',
    },
    { title: 'Coleções', dataIndex: 'editionCount', width: 96 },
    {
      title: 'Ativa',
      dataIndex: 'active',
      width: 80,
      render: (v: boolean, c: CategoryAdmin) => (
        <Switch
          checked={v}
          disabled={busyId === c.id}
          aria-label={`Ativa ${c.name}`}
          onChange={(checked) => handleToggle(c, { active: checked })}
        />
      ),
    },
    {
      title: 'Onboarding',
      dataIndex: 'showInOnboarding',
      width: 110,
      render: (v: boolean, c: CategoryAdmin) => (
        <Switch
          checked={v}
          disabled={busyId === c.id}
          aria-label={`Onboarding ${c.name}`}
          onChange={(checked) => handleToggle(c, { showInOnboarding: checked })}
        />
      ),
    },
    {
      title: 'Ações',
      key: 'acoes',
      width: 160,
      render: (_: unknown, c: CategoryAdmin) => (
        <Space>
          <Button size="small" onClick={() => openEdit(c)}>
            Editar
          </Button>
          <Popconfirm
            title="Excluir esta categoria? As coleções continuam existindo."
            okText="Excluir"
            okButtonProps={{ danger: true }}
            cancelText="Cancelar"
            onConfirm={() => handleDelete(c)}
          >
            <Button size="small" danger>
              Excluir
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Categorias</h2>
        <Button type="primary" onClick={openCreate}>
          Nova categoria
        </Button>
      </div>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={categories}
        pagination={false}
        scroll={{ x: 760 }}
      />

      <Modal
        title={editing ? `Editar categoria #${editing.id}` : 'Nova categoria'}
        visible={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        okText="Salvar"
        cancelText="Cancelar"
        confirmLoading={saving}
        destroyOnClose
        forceRender
      >
        <Form
          form={form}
          layout="vertical"
          preserve={false}
          initialValues={{ active: true, showInOnboarding: true }}
        >
          <Form.Item
            name="name"
            label="Nome"
            rules={[{ required: true, whitespace: true, message: 'Informe o nome da categoria.' }]}
          >
            <Input maxLength={40} placeholder="Ex.: Para relaxar" />
          </Form.Item>
          <Form.Item name="tagline" label="Frase">
            <Input.TextArea showCount maxLength={80} rows={2} />
          </Form.Item>
          <Form.Item name="active" label="Ativa" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="showInOnboarding" label="Aparece no onboarding" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CategoriasCatalog;
